<script setup lang="ts">
import type { AgentSelectProps } from '.';
import type { Pagination } from '~/core/entities/api/ApiPagination';
import type { AgentSearchResult } from '~~/core/entities/AgentSearch';

import { BrokerAvatar } from '@/components/BrokerAvatar';
import { BrokerAvatarInfo } from '@/components/BrokerAvatarInfo';
import {
	Command,
	CommandInput,
	CommandItem,
	CommandList
} from '@/components/SearchSuggestRadixCommand';
import { cn, useStaleGuard } from '@/utils';
import { useDebounceFn } from '@vueuse/core';
import { SEARCH_SUGGEST } from '~/constants';
import { nuxtAgentsRepository } from '~~/adapters/repositories/nuxt';
import { highlightParts } from '~~/utils';

import CancelIcon from '~icons/material-symbols-light/cancel';
import SearchIcon from '~icons/material-symbols-light/search';

import messages from './AgentSelect.locale.json';
import { agentSelectRepositoryKey } from './repository';

const strings = messages.pt;

const props = withDefaults(defineProps<AgentSelectProps>(), {
	selectedAgent: undefined,
	variant: 'primary'
});

const agentsRepository = inject(agentSelectRepositoryKey, nuxtAgentsRepository);

const emit = defineEmits<{
	(e: 'update:selectedAgent', value: AgentSearchResult | undefined): void;
	(e: 'retry'): void;
}>();

// The SRP API filters by the public agent slug, not by the internal id.
const agentSlug = defineModel<string | undefined>({
	default: undefined,
	get: value => value?.trim() || undefined
});

const inputId = useId();
const labelId = useId();
const rootEl = useTemplateRef<HTMLElement>('rootEl');
const clearSelectionButton = useTemplateRef<HTMLButtonElement>(
	'clearSelectionButton'
);
const query = ref('');
const isOpen = ref(false);
const isSearching = ref(false);
const isLoadingMore = ref(false);
const hasLoadMoreError = ref(false);
const pagination = shallowRef<Pagination>();
const hasSearchError = ref(false);
const agents = shallowRef<AgentSearchResult[]>([]);
const selectedAgentState = shallowRef<AgentSearchResult>();
const locallySelectedAgent = shallowRef<AgentSearchResult>();
const lastCompletedTerm = ref('');
const requestGuard = useStaleGuard();
let activeController: AbortController | null = null;

const normalizedQuery = computed(() => query.value.trim());
const hasMinSearchTerm = computed(
	() => normalizedQuery.value.length >= SEARCH_SUGGEST.MIN_SEARCH_LENGTH
);
const hasSelection = computed(() => Boolean(agentSlug.value));
const hasMoreAgents = computed(() =>
	Boolean(
		pagination.value && pagination.value.page < pagination.value.totalPages
	)
);
const hasResolvedSelection = computed(
	() =>
		Boolean(agentSlug.value) &&
		selectedAgentState.value?.slug === agentSlug.value
);
const isSelectionUnavailable = computed(
	() =>
		!hasResolvedSelection.value &&
		props.resolution?.slug === agentSlug.value &&
		props.resolution?.status === 'unavailable'
);
const selectedAgentName = computed(() => selectedAgentState.value?.name || '');
const selectedAgentPhoto = computed(
	() => selectedAgentState.value?.profilePicture.url || ''
);
const selectedAgentCompany = computed(
	() => selectedAgentState.value?.company?.name || ''
);
const shouldShowEmptyState = computed(
	() =>
		!isSearching.value &&
		!hasSearchError.value &&
		hasMinSearchTerm.value &&
		lastCompletedTerm.value === normalizedQuery.value &&
		agents.value.length === 0
);

const isDark = computed(() => props.variant === 'dark');
const titleClasses = computed(() =>
	isDark.value ? 'text-white' : 'text-primary'
);
const inputWrapperClasses = computed(() =>
	isDark.value
		? 'border-primary-light-3 bg-primary-light-4 focus-within:border-white-dark-4'
		: 'border-white-dark-4 bg-white focus-within:border-primary'
);
const inputClasses = computed(() =>
	isDark.value
		? 'text-white placeholder:text-white-dark-4'
		: 'text-primary placeholder:text-primary-light-1'
);
const searchIconClasses = computed(() =>
	isDark.value ? 'text-white' : 'text-primary'
);
const clearSearchClasses = computed(() =>
	isDark.value ? 'text-primary-light-1 hover:text-white' : 'text-white-dark-3'
);
const listClasses = computed(() =>
	isDark.value
		? 'border-primary-light-3 bg-primary-light-4 text-white'
		: 'border-white-dark-4 bg-white text-primary'
);
const mutedTextClasses = computed(() =>
	isDark.value ? 'text-white-dark-4' : 'text-primary-light-2'
);
const itemClasses = computed(() =>
	isDark.value
		? 'text-white data-highlighted:bg-primary-light-3'
		: 'text-primary data-highlighted:bg-white-dark-2'
);

function invalidateSearch() {
	activeController?.abort();
	activeController = null;
	pagination.value = undefined;
	isLoadingMore.value = false;
	hasLoadMoreError.value = false;
	return requestGuard.next();
}

function handleSearchError(page: number) {
	if (page > 1) {
		hasLoadMoreError.value = true;
		return;
	}
	agents.value = [];
	hasSearchError.value = true;
	lastCompletedTerm.value = '';
}

async function searchAgents(term: string, requestId: number, page = 1) {
	// `useDebounceFn` has no public cancellation handle. A queued invocation can
	// still run after selection/unmount, so stop it before creating a request.
	if (requestGuard.isStale(requestId)) return;

	const controller = new AbortController();
	activeController = controller;

	try {
		const [result, error] = await agentsRepository.list(
			{
				q: term,
				hasPublishedProperties: true,
				page,
				perPage: pagination.value?.perPage ?? 10
			},
			controller.signal
		);

		if (controller.signal.aborted || requestGuard.isStale(requestId)) return;

		if (error || !result) {
			handleSearchError(page);
			return;
		}

		const results =
			page === 1 ? result.agents : [...agents.value, ...result.agents];
		agents.value = [
			...new Map(results.map(agent => [agent.id, agent])).values()
		];
		pagination.value = result.pagination;
		hasSearchError.value = false;
		lastCompletedTerm.value = term;
	} catch {
		if (controller.signal.aborted || requestGuard.isStale(requestId)) return;

		handleSearchError(page);
	} finally {
		if (activeController === controller) activeController = null;

		if (!controller.signal.aborted && !requestGuard.isStale(requestId)) {
			isSearching.value = false;
			isLoadingMore.value = false;
		}
	}
}

function loadMoreAgents() {
	if (
		!pagination.value ||
		!hasMoreAgents.value ||
		isSearching.value ||
		isLoadingMore.value ||
		lastCompletedTerm.value !== normalizedQuery.value
	)
		return;

	isLoadingMore.value = true;
	hasLoadMoreError.value = false;
	void searchAgents(
		normalizedQuery.value,
		requestGuard.next(),
		pagination.value.page + 1
	);
}

const debouncedSearch = useDebounceFn(
	(term: string, requestId: number) => searchAgents(term, requestId),
	SEARCH_SUGGEST.TYPING_DEBOUNCE_MS
);

watch(normalizedQuery, term => {
	const requestId = invalidateSearch();
	agents.value = [];
	lastCompletedTerm.value = '';
	hasSearchError.value = false;

	if (term.length < SEARCH_SUGGEST.MIN_SEARCH_LENGTH) {
		isSearching.value = false;
		return;
	}

	// Start the visual loading state before the debounce interval. This avoids a
	// misleading empty-state flash while the user is still typing.
	isSearching.value = true;
	debouncedSearch(term, requestId);
});

watch(
	[agentSlug, () => props.selectedAgent],
	([slug, selectedAgent]) => {
		if (slug && selectedAgent?.slug === slug) {
			selectedAgentState.value = selectedAgent;
			locallySelectedAgent.value = undefined;
		} else if (slug && locallySelectedAgent.value?.slug === slug) {
			// Preserve a choice made inside the unapplied sidebar draft.
			selectedAgentState.value = locallySelectedAgent.value;
		} else {
			selectedAgentState.value = undefined;
			locallySelectedAgent.value = undefined;
		}
	},
	{ immediate: true }
);

function handleFocus() {
	if (!hasSelection.value) isOpen.value = true;
}

function closeDropdown() {
	isOpen.value = false;
}

async function selectAgent(agent: AgentSearchResult) {
	invalidateSearch();
	agentSlug.value = agent.slug;
	locallySelectedAgent.value = agent;
	selectedAgentState.value = agent;
	emit('update:selectedAgent', agent);
	query.value = '';
	agents.value = [];
	lastCompletedTerm.value = '';
	hasSearchError.value = false;
	isSearching.value = false;
	isOpen.value = false;

	await nextTick();
	clearSelectionButton.value?.focus();
}

async function clearSelection() {
	invalidateSearch();
	agentSlug.value = undefined;
	selectedAgentState.value = undefined;
	locallySelectedAgent.value = undefined;
	emit('update:selectedAgent', undefined);
	query.value = '';
	agents.value = [];
	lastCompletedTerm.value = '';
	hasSearchError.value = false;
	isSearching.value = false;

	await nextTick();
	isOpen.value = true;
	document.getElementById(inputId)?.focus();
}

function getCompanyLabel(agent: AgentSearchResult) {
	const companyName = agent.company?.name;
	return companyName
		? strings.company.replace('{company}', companyName)
		: strings.companyFallback;
}

function escapeRegExp(value: string) {
	return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getHighlightedParts(text: string) {
	// highlightParts renders plain text parts (no v-html). Escaping here also
	// makes arbitrary user input such as "[" safe for its internal RegExp.
	return highlightParts(text, escapeRegExp(normalizedQuery.value));
}

onClickOutside(rootEl, closeDropdown);

onBeforeUnmount(invalidateSearch);
</script>

<template>
	<div ref="rootEl" :class="cn('w-full space-y-5', props.class)">
		<p
			:id="labelId"
			:class="[
				'block font-matter-sq text-lg font-normal leading-normal tracking-[-0.36px]',
				titleClasses
			]"
		>
			{{ strings.title }}
		</p>

		<div class="relative min-h-[50px]">
			<div
				v-if="hasSelection"
				:data-testid="
					hasResolvedSelection
						? 'agent-select-selected'
						: 'agent-select-resolution'
				"
				:aria-labelledby="labelId"
				:class="[
					'flex min-h-[50px] w-full items-center gap-2 border px-3 py-2',
					inputWrapperClasses
				]"
			>
				<template v-if="hasResolvedSelection">
					<BrokerAvatar
						class="size-6"
						:broker-src="selectedAgentPhoto"
						:broker-name="selectedAgentName"
						:company-name="selectedAgentCompany"
						:show-company-avatar="false"
					/>
					<span class="min-w-0 flex-1 truncate text-sm font-normal">
						{{ selectedAgentName }}
					</span>
				</template>
				<div
					v-else-if="isSelectionUnavailable"
					data-testid="agent-select-selection-unavailable"
					:class="cn('min-w-0 flex-1 text-sm', titleClasses)"
				>
					<p role="alert">{{ strings.selectionUnavailable }}</p>
					<button
						type="button"
						class="mt-1 rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-primary-light-2"
						@click="emit('retry')"
					>
						{{ strings.retry }}
					</button>
				</div>
				<div
					v-else
					data-testid="agent-select-selection-loading"
					role="status"
					class="flex min-w-0 flex-1 items-center gap-2"
				>
					<span class="sr-only">{{ strings.selectionLoading }}</span>
					<div
						:class="[
							'size-6 shrink-0 animate-pulse rounded-full',
							isDark ? 'bg-primary-light-3' : 'bg-white-dark-3'
						]"
						aria-hidden="true"
					/>
					<div
						:class="[
							'h-4 w-2/3 animate-pulse rounded',
							isDark ? 'bg-primary-light-3' : 'bg-white-dark-3'
						]"
						aria-hidden="true"
					/>
				</div>
				<button
					ref="clearSelectionButton"
					type="button"
					:class="
						cn(
							'grid size-8 shrink-0 place-items-center rounded-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary-light-2',
							clearSearchClasses
						)
					"
					:aria-label="strings.clear"
					@mousedown.prevent
					@click="clearSelection"
				>
					<CancelIcon
						data-testid="agent-select-clear-icon"
						class="size-5"
						aria-hidden="true"
					/>
				</button>
			</div>

			<Command
				v-else
				v-model:open="isOpen"
				class="absolute z-50 h-auto w-full overflow-visible rounded-none bg-transparent"
				@keydown.esc.stop="closeDropdown"
			>
				<CommandInput
					:id="inputId"
					v-model="query"
					data-testid="agent-select-input"
					:aria-labelledby="labelId"
					aria-autocomplete="list"
					autocomplete="off"
					:placeholder="strings.placeholder"
					:wrapper-classes="cn('h-[50px] border', inputWrapperClasses)"
					:class="cn('h-[48px] px-3 text-sm', inputClasses)"
					@focus="handleFocus"
				>
					<template #prefix>
						<div class="grid h-full place-items-center pl-3">
							<SearchIcon
								data-testid="agent-select-search-icon"
								:class="cn('size-5', searchIconClasses)"
								aria-hidden="true"
							/>
						</div>
					</template>
					<template #suffix>
						<button
							v-show="query"
							type="button"
							data-testid="agent-select-clear-search"
							:class="
								cn(
									'mr-2 grid size-8 shrink-0 place-items-center rounded-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary-light-2',
									clearSearchClasses
								)
							"
							aria-label="Limpar busca"
							@pointerdown.stop.prevent="query = ''"
							@mousedown.stop.prevent
							@click.stop="query = ''"
						>
							<CancelIcon
								data-testid="agent-select-clear-icon"
								class="size-5"
								aria-hidden="true"
							/>
						</button>
					</template>
				</CommandInput>

				<CommandList
					v-if="isOpen"
					:aria-busy="isSearching || isLoadingMore"
					:class="
						cn(
							'max-h-[215px] border border-t-0 p-0 shadow-[0_1px_3px_1px_#00000026]',
							listClasses
						)
					"
				>
					<div
						v-if="!hasMinSearchTerm"
						data-testid="agent-select-instruction"
						role="status"
						class="flex min-h-[150px] flex-col justify-center px-4 py-5 text-center"
					>
						<p class="text-base font-medium">{{ strings.instructionTitle }}</p>
						<p :class="cn('mt-1 text-sm', mutedTextClasses)">
							{{ strings.instruction }}
						</p>
					</div>

					<div
						v-else-if="isSearching"
						data-testid="agent-select-loading"
						class="space-y-1 py-1"
						role="status"
						aria-live="polite"
					>
						<span class="sr-only">{{ strings.loading }}</span>
						<div
							v-for="index in 3"
							:key="`agent-loading-${index}`"
							class="flex items-center gap-3 px-4 py-2"
							aria-hidden="true"
						>
							<div
								:class="[
									'size-10 shrink-0 animate-pulse rounded-full',
									isDark ? 'bg-primary-light-3' : 'bg-white-dark-3'
								]"
							/>
							<div class="flex-1 space-y-2">
								<div
									:class="[
										'h-4 w-2/5 animate-pulse rounded',
										isDark ? 'bg-primary-light-3' : 'bg-white-dark-3'
									]"
								/>
								<div
									:class="[
										'h-3 w-3/5 animate-pulse rounded',
										isDark ? 'bg-primary-light-2' : 'bg-white-dark-2'
									]"
								/>
							</div>
						</div>
					</div>

					<div
						v-else-if="hasSearchError || shouldShowEmptyState"
						:data-testid="
							hasSearchError ? 'agent-select-error' : 'agent-select-empty'
						"
						class="flex min-h-[150px] flex-col items-center justify-center gap-2 px-4 py-5 text-center font-sans"
						:role="hasSearchError ? 'alert' : 'status'"
						:aria-live="hasSearchError ? undefined : 'polite'"
					>
						<p class="w-full max-w-80 text-base font-normal leading-[1.5]">
							{{ hasSearchError ? strings.error : strings.empty }}
						</p>
						<p
							:class="
								cn(
									'w-full max-w-80 text-sm font-normal leading-[1.5]',
									isDark ? 'text-white-dark-4' : 'text-primary-light-3'
								)
							"
						>
							{{
								hasSearchError
									? strings.errorDescription
									: strings.emptyDescription
							}}
						</p>
					</div>

					<div v-else-if="agents.length" data-testid="agent-select-results">
						<CommandItem
							v-for="agent in agents"
							:key="agent.id"
							:value="agent"
							:aria-label="`${agent.name}, ${getCompanyLabel(agent)}`"
							:class="cn('rounded-none px-4 py-3', itemClasses)"
							@select="selectAgent(agent)"
						>
							<BrokerAvatarInfo
								class="min-w-0 gap-3"
								avatar-class="size-10"
								:broker-src="agent.profilePicture.url"
								:broker-name="agent.name"
								:company-name="agent.company?.name || ''"
								:show-company-avatar="false"
								:info-classes="{
									wrapper: 'min-w-0',
									brokerName: 'text-base font-normal',
									companyName: cn('text-sm', mutedTextClasses)
								}"
							>
								<template #broker>
									<template
										v-for="(part, index) in getHighlightedParts(agent.name)"
										:key="`${agent.id}-name-${index}`"
									>
										<strong v-if="part.highlight" class="font-semibold">
											{{ part.text }}
										</strong>
										<span v-else>{{ part.text }}</span>
									</template>
								</template>
								<template #company>
									<template
										v-for="(part, index) in getHighlightedParts(
											getCompanyLabel(agent)
										)"
										:key="`${agent.id}-company-${index}`"
									>
										<strong v-if="part.highlight" class="font-semibold">
											{{ part.text }}
										</strong>
										<span v-else>{{ part.text }}</span>
									</template>
								</template>
							</BrokerAvatarInfo>
						</CommandItem>
					</div>
				</CommandList>
				<div
					v-if="isOpen && hasMoreAgents && !isSearching && !hasSearchError"
					:class="
						cn('border border-t-0 px-4 py-2 text-center text-sm', listClasses)
					"
				>
					<p
						v-if="hasLoadMoreError"
						role="alert"
						data-testid="agent-select-more-error"
					>
						{{ strings.loadMoreError }}
					</p>
					<button
						type="button"
						data-testid="agent-select-load-more"
						class="w-full py-2 underline outline-none focus-visible:ring-2 focus-visible:ring-primary-light-2 disabled:opacity-50"
						:disabled="isLoadingMore"
						@mousedown.prevent
						@click.stop="loadMoreAgents"
					>
						{{
							isLoadingMore
								? strings.loading
								: hasLoadMoreError
									? strings.retry
									: strings.loadMore
						}}
					</button>
				</div>
			</Command>
		</div>
	</div>
</template>
